class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  cartUpdateUnsubscriber = undefined;
  cartUpdateUnsubscriberForLocationBasedContent = undefined;
  bevmoLocationUpdateUnsubscriberForLocationBasedContent = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.cartUpdateUnsubscriberForLocationBasedContent = subscribe(PUB_SUB_EVENTS.cartUpdate, this.displayLocationBasedContent.bind(this));
    this.bevmoLocationUpdateUnsubscriberForLocationBasedContent = subscribe(PUB_SUB_EVENTS.bevmoLocationUpdate, this.displayLocationBasedContent.bind(this));
    this.displayLocationBasedContent();

    if (window.location.hash.includes('#cart')) {
      this.open();
    }
  }

  displayLocationBasedContent() {
    this.querySelectorAll('.visible-for-delivery, .visible-for-pickup, .visible-for-shipping').forEach(item => {
      item.classList.add('hidden');
    });

    this.querySelectorAll(`.visible-for-${window.bevmoLocation.fulfillment_type}`).forEach(item => {
      item.classList.remove('hidden');
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.cartUpdateUnsubscriberForLocationBasedContent) {
      this.cartUpdateUnsubscriberForLocationBasedContent();
    }

    if (this.bevmoLocationUpdateUnsubscriberForLocationBasedContent) {
      this.bevmoLocationUpdateUnsubscriberForLocationBasedContent();
    }
  }

  onCartUpdate(event) {
    if (event.cart) {
      this.classList.toggle('is-empty', event.cart.item_count === 0);
    }
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[data-cart-icon-bubble]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const searchModal = document.querySelector(`#SearchModal`);
        if (searchModal) {
          searchModal.hide();
        }
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.isOpen = true;

    gopuffCustomAnalytics.publishEvent(`gopuff_cart_opened`, {});
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.isOpen = false;
    const bottomNav = document.querySelector(`bottom-nav`);
    if (bottomNav) {
      bottomNav.fallbackBottomItems();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, open = true) {
    const cartData = parsedState.token ? parsedState : null;

    if (cartData) {
      window.cart = cartData;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.displayLocationBasedContent();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (open) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-icon-bubble-mobile',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate(event) {
    if (event.cart) {
      this.classList.toggle('is-empty', event.cart.item_count === 0);
    }

    fetch(`/cart?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-drawer-items');
        this.innerHTML = sourceQty.innerHTML;
        this.checkProductAvailability();
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble-mobile',
        section: 'cart-icon-bubble-mobile',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
